import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, Repeat, Sparkles } from "lucide-react";

const CONDITIONS = [
  "PSA 10", "PSA 9", "PSA 8", "PSA 7",
  "BGS 10", "BGS 9.5", "BGS 9",
  "CGC 10", "CGC 9.5",
  "Raw NM", "Raw LP", "Raw MP", "Raw HP",
  "Other",
];

export default function TradePage() {
  const { user, isCustomer } = useAuth();
  const [form, setForm] = useState({
    card_name: "", set_name: "", cert_number: "", condition: "Raw NM",
    estimated_value: "", image_url: "", looking_for: "",
    owner_name: "", owner_email: "", owner_phone: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  // Auto-fill from profile when customer is logged in
  /* eslint-disable */
  useEffect(() => {
    if (isCustomer && user) {
      setForm((f) => ({
        ...f,
        owner_name: f.owner_name || user.name || "",
        owner_email: f.owner_email || user.email || "",
        owner_phone: f.owner_phone || user.phone || "",
      }));
    }
  }, [isCustomer, user]);
  /* eslint-enable */

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = { ...form, estimated_value: Number(form.estimated_value) || 0 };
      const { data } = await api.post("/trades", payload);
      setDone(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="trade-success" className="max-w-2xl mx-auto px-4 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-yellow-400 mx-auto" />
        <h1 className="mt-6 font-[Outfit] text-4xl font-black">Trade request received!</h1>
        <p className="mt-4 text-slate-400">
          We&apos;ll review <span className="text-white font-semibold">{done.card_name}</span> and reach out to{" "}
          <span className="text-white">{done.owner_email}</span> if we can match it.
        </p>
        <div className="mt-8 bg-slate-900 border border-slate-800 rounded-xl p-6 text-left">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Submission</div>
          <div className="text-white font-semibold">{done.card_name} · {done.condition}</div>
          {done.cert_number && <div className="text-xs text-slate-500 mt-1 font-mono">Cert: {done.cert_number}</div>}
          <div className="text-sm text-slate-400 mt-2">Looking for: {done.looking_for || "—"}</div>
          <div className="text-xs text-slate-500 mt-3 font-mono">Trade ID: {done.id}</div>
        </div>
        <Link to="/" data-testid="trade-success-home" className="mt-8 inline-block text-yellow-400 hover:text-yellow-300 font-mono text-sm uppercase tracking-wider">
          ← Back home
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="trade-page" className="max-w-3xl mx-auto px-4 sm:px-8 py-16">
      <div className="text-xs tracking-[0.25em] uppercase font-mono text-yellow-400 mb-3">
        <Repeat className="w-3.5 h-3.5 inline mr-1" />
        // Trading Arena
      </div>
      <h1 className="font-[Outfit] text-4xl sm:text-5xl font-black tracking-tighter">
        Got cards to trade?
      </h1>
      <p className="mt-3 text-slate-400">
        Send us what you&apos;ve got and what you&apos;re after — we take trade-ins at{" "}
        <span className="text-yellow-400 font-bold">80% market value</span>.
        Singles, slabs, sealed — all good.
      </p>

      <div
        data-testid="trade-rate-banner"
        className="mt-6 bg-slate-900 border border-yellow-400/40 rounded-xl p-4 flex items-center gap-4"
      >
        <Repeat className="w-6 h-6 text-yellow-400 shrink-0" />
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-yellow-400">
            Our trade rate
          </div>
          <div className="text-white font-bold">
            Trade-in at 80% market value
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            We price from eBay last-solds and Collectr — you always know how we got the number.
          </div>
        </div>
      </div>

      {!isCustomer && (
        <div className="mt-6 bg-yellow-400/10 border border-yellow-400/30 rounded-xl p-4 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-yellow-400 mt-0.5 shrink-0" />
          <div className="text-sm text-slate-300">
            <Link to="/signin" className="text-yellow-400 font-bold hover:text-yellow-300">Sign in</Link>{" "}
            or{" "}
            <Link to="/signup" className="text-yellow-400 font-bold hover:text-yellow-300">create an account</Link>{" "}
            to auto-fill your info and track all your trade requests.
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-6">
        {/* Card details */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">The card / slab</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Card name" required>
              <input data-testid="trade-card-name" required value={form.card_name} onChange={(e) => setField("card_name", e.target.value)} className={input} placeholder="e.g. Charizard ex Alt Art" />
            </Field>
            <Field label="Set">
              <input data-testid="trade-set" value={form.set_name} onChange={(e) => setField("set_name", e.target.value)} className={input} placeholder="e.g. Obsidian Flames" />
            </Field>
            <Field label="Condition / Grade">
              <select data-testid="trade-condition" value={form.condition} onChange={(e) => setField("condition", e.target.value)} className={input}>
                {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Cert # (if graded)">
              <input data-testid="trade-cert" value={form.cert_number} onChange={(e) => setField("cert_number", e.target.value)} className={input} placeholder="e.g. 12345678" />
            </Field>
            <Field label="Estimated value (CAD)">
              <input data-testid="trade-value" type="number" step="0.01" value={form.estimated_value} onChange={(e) => setField("estimated_value", e.target.value)} className={input} placeholder="0.00" />
            </Field>
            <Field label="Photo URL">
              <input data-testid="trade-image" value={form.image_url} onChange={(e) => setField("image_url", e.target.value)} className={input} placeholder="https://..." />
            </Field>
          </div>
          <p className="text-xs text-slate-500">
            Tip: upload your photo to{" "}
            <a href="https://imgur.com/upload" target="_blank" rel="noreferrer" className="text-yellow-400 hover:text-yellow-300 underline">imgur.com</a>{" "}
            then paste the direct image link here.
          </p>
        </div>

        {/* What they want */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">What are you looking for?</h2>
          <Field label="In return I want…" required>
            <textarea data-testid="trade-looking-for" required rows={3} value={form.looking_for} onChange={(e) => setField("looking_for", e.target.value)} className={input}
              placeholder="e.g. Pikachu Alt Art, OR cash $400 CAD, OR Lugia VStar Alt + cash diff" />
          </Field>
        </div>

        {/* Contact */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">Your contact</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name" required>
              <input data-testid="trade-owner-name" required value={form.owner_name} onChange={(e) => setField("owner_name", e.target.value)} className={input} />
            </Field>
            <Field label="Email" required>
              <input data-testid="trade-owner-email" type="email" required value={form.owner_email} onChange={(e) => setField("owner_email", e.target.value)} className={input} />
            </Field>
            <Field label="Phone (optional)">
              <input data-testid="trade-owner-phone" value={form.owner_phone} onChange={(e) => setField("owner_phone", e.target.value)} className={input} />
            </Field>
          </div>
          <Field label="Notes (optional)">
            <textarea data-testid="trade-notes" rows={2} value={form.notes} onChange={(e) => setField("notes", e.target.value)} className={input} />
          </Field>
        </div>

        {error && (
          <div data-testid="trade-error" className="text-red-400 bg-red-500/10 border border-red-500/30 px-4 py-3 rounded-md text-sm">{error}</div>
        )}

        <button data-testid="trade-submit" type="submit" disabled={submitting} className="w-full bg-yellow-400 text-slate-950 font-bold px-6 py-4 rounded-md uppercase tracking-wide hover:bg-yellow-300 transition-colors disabled:opacity-50">
          {submitting ? "Sending…" : "Submit trade request"}
        </button>
      </form>
    </div>
  );
}

const input = "w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-yellow-400";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}
